import { useEffect, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react'
import type { AuthSession, SaveDocument } from '../api'
import type { GameState } from '../game'
import { PanelContent } from './SupportPanels'
import { cloudStatusText, type CloudStatus, type SaveSelection } from './view-types'
import { usePlayerAccess } from './PlayerAccess'
import { compressAvatar } from '../avatar-image'
import { AvatarCropDialog } from './AvatarCropDialog'

type AccountPanelProps = {
  state: GameState | null
  locationLabel: string
  session: AuthSession | null
  loading: boolean
  error: string
  cloudStatus: CloudStatus
  cloudDocument: SaveDocument | null
  saveSelection: SaveSelection | null
  cloudError: string
  cloudActionError: string
  cloudActionBusy: boolean
  onLogin: () => void
  onLogout: () => void
  onReset: () => void
  onSync: () => void
  onChooseGuest: () => void
  onChooseCloud: () => void
  onRefreshAuth: () => void
  onResetCloud: (scope: 'run' | 'all') => void
}

type ProgressState = GameState | GameState['coreState']

function progressSummary(state: ProgressState): string {
  const value = state as Record<string, unknown>
  const totalPlays = Number(value.totalPlays || 0)
  const nodeIdx = Number(value.nodeIdx || 0)
  const achievements = Array.isArray(value.achievements) ? value.achievements.length : 0
  const endings = (Array.isArray(value.endingsCollected) ? value.endingsCollected.length : 0)
    + (Array.isArray(value.fateCollected) ? value.fateCollected.length : 0)
  return `第 ${Math.max(totalPlays, 1)} 次人生 · 当前节点 ${nodeIdx + 1} · ${achievements} 项成就 · ${endings} 个结局`
}

function SaveChoiceCard({ title, label, updatedAt, state, onChoose, disabled }: {
  title: string
  label: '较新' | '较旧'
  updatedAt: string
  state: ProgressState
  onChoose: () => void
  disabled: boolean
}) {
  return (
    <button className="save-choice-card" data-recency={label === '较新' ? 'newer' : 'older'} onClick={onChoose} disabled={disabled}>
      <span className="save-choice-heading"><strong>{title}</strong><em>{label}</em></span>
      <time dateTime={updatedAt}>{new Date(updatedAt).toLocaleString()}</time>
      <small>{progressSummary(state)}</small>
      <span className="save-choice-action">保留这份存档</span>
    </button>
  )
}

export function AccountPanel({ state, locationLabel, session, loading, error, cloudStatus, cloudDocument, saveSelection, cloudError, cloudActionError, cloudActionBusy, onLogin, onLogout, onReset, onSync, onChooseGuest, onChooseCloud, onRefreshAuth, onResetCloud }: AccountPanelProps) {
  const user = session?.user
  const profile = usePlayerAccess()
  const [nameDraft, setNameDraft] = useState(profile.nickname)
  const [editError, setEditError] = useState('')
  const [saveNotice, setSaveNotice] = useState('')
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  useEffect(() => setNameDraft(profile.nickname), [profile.nickname])
  const saveName = async (event: FormEvent) => {
    event.preventDefault()
    const value = nameDraft.normalize('NFKC').trim()
    if (!/^[\p{L}\p{N}· _-]{1,12}$/u.test(value)) { setSaveNotice(''); setEditError('请输入 1–12 个字，可用中英文、数字、空格、下划线或短横线'); return }
    setEditError('')
    setSaveNotice('')
    if (await profile.submitName(value)) setSaveNotice('名字保存成功')
  }
  const chooseAvatar = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (!/^image\/(?:jpeg|png|webp)$/.test(file.type)) { setEditError('请选择 JPG、PNG 或 WebP 图片'); return }
    if (!file.size || file.size > 12 * 1024 * 1024) { setEditError('原图不能超过 12 MB'); return }
    setEditError(''); setSaveNotice(''); setAvatarFile(file)
  }
  const saveAvatar = async (crop: Parameters<typeof compressAvatar>[1]) => {
    if (!avatarFile) return
    try {
      if (await profile.submitAvatar(await compressAvatar(avatarFile, crop))) {
        setAvatarFile(null)
        setSaveNotice('头像保存成功')
      }
    } catch (reason) { setEditError(reason instanceof Error ? reason.message : '头像处理失败，请重试') }
  }
  const playerName = profile.nickname || '未设置名字'
  const avatar = user ? playerName.slice(0, 1).toUpperCase() : '访客'
  const guestIsNewer = Boolean(saveSelection && Date.parse(saveSelection.guest.updatedAt) > Date.parse(saveSelection.cloud.updatedAt))
  const items: ReactNode[] = [
    <div className="account-identity" key="identity">{user ? <label className="account-avatar account-avatar-edit" aria-label="更换头像"><input type="file" accept="image/jpeg,image/png,image/webp" onChange={chooseAvatar} disabled={profile.busy} />{profile.avatarUrl ? <img src={profile.avatarUrl} alt="" /> : <span>{avatar}</span>}<small>更换</small></label> : <div className="account-avatar" aria-hidden="true">{avatar}</div>}<h3>{user ? playerName : '游客档案'}</h3>{user && <form className="profile-name-editor" onSubmit={event => void saveName(event)}><label htmlFor="profile-nickname">用户名称</label><div><input id="profile-nickname" value={nameDraft} onChange={event => { setNameDraft(event.target.value); setSaveNotice('') }} maxLength={24} autoComplete="nickname" disabled={profile.busy} /><button className="secondary-button" type="submit" disabled={profile.busy || nameDraft.trim() === profile.nickname}>{profile.busy ? '保存中…' : '保存'}</button></div>{saveNotice && <small className="profile-save-success" role="status">{saveNotice}</small>}</form>}<p>{user ? '头像会压缩后保存到云端；名称和头像会跟随账号显示。' : '当前使用游客存档，只保存在这台设备。登录后可以保存到云端。'}</p>{(editError || profile.error) && <p className="inline-error" role="alert">{editError || profile.error}</p>}{avatarFile && <AvatarCropDialog file={avatarFile} busy={profile.busy} onCancel={() => setAvatarFile(null)} onConfirm={crop => void saveAvatar(crop)} />}</div>,
    <div className="account-status" key="status"><span>{user ? '用户名称' : '保存位置'}</span><strong>{user ? playerName : locationLabel}</strong><small>{user ? '已登录，云存档同步已启用' : (state ? '游客进度已保存' : '还没有开始人生')}</small></div>,
    ...(error ? [<p className="inline-error" role="alert" key="auth-error">{error}</p>] : []),
    user ? <button className="secondary-button full" onClick={onLogout} disabled={loading} key="logout">{loading ? '正在退出…' : '退出账号'}</button> : <button className="primary-button full" onClick={onLogin} disabled={loading || !session?.auth.configured} key="login">{loading ? '正在连接账号服务…' : session?.auth.configured ? '手机号登录' : '登录服务暂不可用'}</button>,
    <div className="cloud-status-card" key="cloud-status"><div><strong>{user ? '最新云存档' : '云存档'}</strong><span className={`cloud-status ${cloudStatus}`}>{user ? cloudStatusText(cloudStatus) : '登录后可用'}</span></div>{user && cloudDocument && <small>最后保存：{new Date(cloudDocument.updatedAt).toLocaleString()}</small>}</div>
  ]

  if (user && cloudError) items.push(<p className="inline-error" role="status" key="cloud-error">{cloudError}</p>)
  if (user && cloudActionError) items.push(<p className="inline-error" role="alert" key="action-error">{cloudActionError}</p>)

  if (user && saveSelection) {
    items.push(
      <div className="save-choice-panel" key="save-choice">
        <div className="save-choice-intro"><strong>选择唯一保留的存档</strong><p>只按保存时间标记新旧。选择后，另一份会被删除，内容不会合并。</p></div>
        <SaveChoiceCard title="游客存档" label={guestIsNewer ? '较新' : '较旧'} updatedAt={saveSelection.guest.updatedAt} state={saveSelection.guest.state} onChoose={onChooseGuest} disabled={cloudActionBusy} />
        <SaveChoiceCard title="云端存档" label={guestIsNewer ? '较旧' : '较新'} updatedAt={saveSelection.cloud.updatedAt} state={saveSelection.cloud.state} onChoose={onChooseCloud} disabled={cloudActionBusy} />
      </div>
    )
  } else if (user) {
    items.push(<button className="secondary-button full" onClick={onSync} disabled={loading || cloudStatus === 'loading' || cloudStatus === 'pending' || cloudActionBusy || !state} key="sync">{cloudStatus === 'pending' ? '正在同步…' : cloudStatus === 'offline' ? '重新连接并同步' : '立即同步'}</button>)
    items.push(<div className="cloud-action-row" key="cloud-reset"><button className="text-button" onClick={() => onResetCloud('run')} disabled={cloudActionBusy}>重开本局</button><button className="text-button" onClick={() => onResetCloud('all')} disabled={cloudActionBusy}>清除全部进度</button></div>)
  }

  items.push(<p className="privacy-note" key="privacy">{user ? '云端不会保存历史版本。同步完成后只能读取最后一次保存的进度。' : '手机号首次验证成功后会自动创建账号。'} <a className="privacy-link" href="/privacy-policy.html">隐私政策</a>{!user && error && <button className="text-button" onClick={onRefreshAuth}>重新连接账号服务</button>}</p>)
  if (!user) items.push(<button className="danger-button full" onClick={onReset} key="reset">清除游客存档</button>)
  return <PanelContent items={items} label="账号" className="account-panel" />
}
